import { useRef, useState, type ReactNode } from "react";

import { useDialogFocusTrap } from "@/hooks/useDialogFocusTrap";
import {
  formatPortalUserDate,
  type AdminPortalUser,
} from "@/lib/api/admin-portal-users";
import {
  ACCESS_ROLE_OPTIONS,
  accountTypeChangeSummary,
  type AccessRole,
} from "@/lib/api/portal-access-role";
import {
  AdminTypePill,
  PortalUserStatusBadge,
} from "@/pages/admin/AdminUserBadges";

export type PortalUserDrawerStep = "details" | "edit";

type PortalUserDetailsDrawerProps = {
  user: AdminPortalUser;
  closing?: boolean;
  initialStep: PortalUserDrawerStep;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  /** Resolves true when the account type was persisted. */
  onSave: (accessRole: AccessRole) => Promise<boolean>;
  onViewRequests: (user: AdminPortalUser) => void;
};

function Field({
  label,
  testId,
  full,
  children,
}: {
  label: string;
  testId?: string;
  full?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={`dfield${full ? " full" : ""}`}>
      <div className="dlabel">{label}</div>
      <div className="dval" data-testid={testId}>
        {children}
      </div>
    </div>
  );
}

export function PortalUserDetailsDrawer({
  user,
  closing = false,
  initialStep,
  submitting,
  error,
  onClose,
  onSave,
  onViewRequests,
}: PortalUserDetailsDrawerProps) {
  const [editingType, setEditingType] = useState(initialStep === "edit");
  const [draftRole, setDraftRole] = useState<AccessRole | "">(
    user.access_role ?? "",
  );
  const drawerRef = useRef<HTMLElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);

  function requestClose() {
    if (submitting) {
      return;
    }
    onClose();
  }

  useDialogFocusTrap(!closing, drawerRef, requestClose, closeRef, {
    restoreFocus: false,
  });

  const roleSelected = draftRole === "customer" || draftRole === "partner";
  const hasChange = roleSelected && draftRole !== user.access_role;

  function startEditing() {
    setDraftRole(user.access_role ?? "");
    setEditingType(true);
  }

  function stopEditing() {
    setEditingType(false);
    setDraftRole(user.access_role ?? "");
  }

  async function save() {
    if (!hasChange) {
      return;
    }
    const saved = await onSave(draftRole);
    if (saved) {
      setEditingType(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className={`drawer-backdrop${closing ? " drawer-backdrop--closing" : ""}`}
        aria-label="Close portal user details"
        data-testid="admin-user-drawer-backdrop"
        onClick={requestClose}
      />
      <aside
        ref={drawerRef}
        className={`detail-drawer${closing ? " detail-drawer--closing" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-user-drawer-title"
        data-testid="admin-user-drawer"
      >
        <div className="drawer-top">
          <div className="drawer-heading">
            <p className="drawer-kicker">Portal user</p>
            <h2 id="admin-user-drawer-title" data-testid="admin-user-drawer-title">
              {user.contact_name}
            </h2>
            <div className="drawer-meta" data-testid="admin-user-drawer-meta">
              <PortalUserStatusBadge status={user.status} />
              <AdminTypePill type={user.access_role} />
              <span className="drawer-submitted">
                Registered {formatPortalUserDate(user.created_at)}
              </span>
            </div>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="drawer-close"
            data-testid="admin-user-drawer-close"
            onClick={requestClose}
          >
            <i className="ti ti-x" aria-hidden="true" />
            Close
          </button>
        </div>

        <div className="drawer-body">
          <div data-testid={`admin-user-detail-${user.id}`}>
            <div className="detail-grid">
              <div className="dfield">
                <div className="dlabel">Applicant</div>
                <div className="dval">{user.contact_name}</div>
                <div className="dval muted" data-testid="admin-user-detail-email">
                  {user.email}
                </div>
              </div>
              <Field label="Business" testId="admin-user-detail-company">
                {user.company_name}
              </Field>
            </div>

            <hr className="divider" />

            <div className="dfield full">
              <div className="dlabel">Account type</div>
              {editingType ? (
                <>
                  <div className="dval type-edit-row">
                    <select
                      className="type-select"
                      aria-label={`Account type for ${user.contact_name}`}
                      data-testid="admin-user-access-role"
                      value={draftRole}
                      disabled={submitting}
                      onChange={(event) => {
                        const next = event.target.value;
                        setDraftRole(
                          next === "customer" || next === "partner" ? next : "",
                        );
                      }}
                    >
                      {!user.access_role ? (
                        <option value="">Not set</option>
                      ) : null}
                      {ACCESS_ROLE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="type-edit-btn"
                      data-testid="admin-user-type-cancel"
                      disabled={submitting}
                      onClick={stopEditing}
                    >
                      Cancel
                    </button>
                  </div>

                  {hasChange ? (
                    <div className="type-confirm" data-testid="admin-user-type-confirm">
                      <p
                        className="type-confirm__copy"
                        data-testid="admin-user-type-confirm-copy"
                      >
                        {`${accountTypeChangeSummary(
                          user.contact_name,
                          user.access_role,
                          draftRole,
                        )} Their access requests will use this type. Save this change?`}
                      </p>
                      <button
                        type="button"
                        className="review-modal__btn review-modal__btn--approve"
                        data-testid="admin-user-type-save"
                        disabled={submitting}
                        onClick={() => {
                          void save();
                        }}
                      >
                        {submitting ? "Saving…" : "Save changes"}
                      </button>
                    </div>
                  ) : null}

                  {error ? (
                    <p
                      className="review-modal__error"
                      data-testid="admin-user-type-error"
                      role="alert"
                    >
                      {error}
                    </p>
                  ) : null}
                </>
              ) : (
                <div className="dval type-edit-row" data-testid="admin-user-detail-type">
                  <button
                    type="button"
                    className="type-pill-btn"
                    aria-label={`Change account type for ${user.contact_name}`}
                    data-testid="admin-user-detail-type-pill"
                    onClick={startEditing}
                  >
                    <AdminTypePill type={user.access_role} />
                  </button>
                  <button
                    type="button"
                    className="type-edit-btn"
                    data-testid="admin-user-detail-edit-type"
                    onClick={startEditing}
                  >
                    <i className="ti ti-pencil" aria-hidden="true" />
                    Edit
                  </button>
                </div>
              )}
            </div>

            <hr className="divider" />

            <div className="detail-grid">
              <Field label="Status" testId="admin-user-detail-status">
                <PortalUserStatusBadge status={user.status} />
              </Field>
              <Field label="Registered" testId="admin-user-detail-registered">
                {formatPortalUserDate(user.created_at)}
              </Field>
            </div>

            <hr className="divider" />

            <Field
              label="Access requests"
              full
              testId="admin-user-detail-requests"
            >
              <span className="dval--with-copy">
                {user.access_request_count}
                {user.access_request_count > 0 ? (
                  <button
                    type="button"
                    className="type-edit-btn"
                    data-testid="admin-user-detail-view-requests"
                    onClick={() => onViewRequests(user)}
                  >
                    View requests
                    <i className="ti ti-chevron-right" aria-hidden="true" />
                  </button>
                ) : null}
              </span>
            </Field>

            <hr className="divider" />

            <Field label="User ID" full testId="admin-user-detail-id">
              <code className="request-id-code">{user.id}</code>
            </Field>
          </div>
        </div>
      </aside>
    </>
  );
}
